import { useRef } from 'react';
import { Camera, X, Plus, Images, ChevronDown, Upload, Tag, MessageCircle } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';
import { FormField, FormItem, FormControl, FormMessage } from '@/components/ui/form';
import { FloatingInput, FloatingSelectField } from '@/components/ui/floating-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { TRADING_CARD_GAMES } from '@/constants/tradingCardGames';
import { GRADING_COMPANIES } from '@/constants/gradingCompanies';
import { CATEGORY_LABELS, type ListingCategory } from '@/types/listing';
import { PRICE_INPUT_PROPS } from '@/lib/priceInputProps';

const CATEGORIES: { value: string; label: string }[] = (
  Object.entries(CATEGORY_LABELS) as [ListingCategory, string][]
)
  .filter(([v]) => v !== 'lots')
  .map(([value, label]) => ({ value, label }));
import { useCardMatchCandidates } from '@/hooks/useCardMatchCandidates';
import { cn } from '@/lib/utils';
import type { ItemFormData } from './itemFormSchema';

type OrderableImage =
  | { type: 'uploaded'; index: number; url: string }
  | { type: 'stock'; url: string };

interface FastAddRowProps {
  form: UseFormReturn<ItemFormData>;
  newImagePreviews: string[];
  setNewImages: React.Dispatch<React.SetStateAction<File[]>>;
  setNewImagePreviews: React.Dispatch<React.SetStateAction<string[]>>;
  onContinueEditing?: () => void;
  /** When true, the expanded section owns pricing — FastAddRow suppresses its
   *  inline Price + List controls to avoid duplicate fields. */
  expanded?: boolean;
  addedCount: number;
  stockImageUrl?: string | null;
  setStockImageUrl?: (url: string | null) => void;
  onOpenStockPicker?: () => void;
  // Multi-photo additions — when present, renders thumb strip + "Add more" chips below the hero.
  totalImageCount?: number;
  orderableImages?: OrderableImage[];
  onAddFiles?: (files: File[]) => void | Promise<void>;
  onRemoveUploaded?: (index: number) => void;
  onRemoveStock?: () => void;
}

/**
 * Compact fast-add row — the default view when opening Add Item.
 * Photo hero + Game + Type + Title (+ Grade row when Type=Slab).
 * When 1+ photos exist, renders a thumbnail strip and field-surface "Add more"
 * chips (Take / Upload / Sample) directly below the hero.
 * Match candidate slot is wired but hidden until the hook returns candidates.
 */
export function FastAddRow({
  form,
  newImagePreviews,
  setNewImages,
  setNewImagePreviews,
  onContinueEditing,
  expanded = false,
  addedCount,
  stockImageUrl,
  setStockImageUrl,
  onOpenStockPicker,
  totalImageCount = 0,
  orderableImages = [],
  onAddFiles,
  onRemoveUploaded,
  onRemoveStock,
}: FastAddRowProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const addMoreInputRef = useRef<HTMLInputElement>(null);
  const addMoreCameraRef = useRef<HTMLInputElement>(null);
  const uploadedPhoto = newImagePreviews[0] || null;
  const photo = uploadedPhoto || stockImageUrl || null;
  const isStock = !uploadedPhoto && Boolean(stockImageUrl);
  const hasAnyPhoto = totalImageCount > 0 || Boolean(photo);
  const canAddMore = totalImageCount < 10;

  const watchGame = form.watch('game');
  const watchCategory = form.watch('category');
  const watchTitle = form.watch('title');
  const watchGradingCompany = form.watch('grading_company');
  const watchSlabGrade = form.watch('slab_grade');
  const watchIsListed = form.watch('is_listed');
  const watchIsNegotiable = form.watch('is_negotiable');

  // Match seam — today returns []. Future: Scrydex candidates render below Title.
  const { candidates } = useCardMatchCandidates({
    game: watchGame || null,
    category: (watchCategory as any) || null,
    title: watchTitle || null,
    grading_company: watchGradingCompany || null,
    grade: watchSlabGrade ? String(watchSlabGrade) : null,
    image_url: photo,
  });

  // Hero photo handler — accepts multiple: first becomes hero, rest are appended.
  const handleHeroPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.target;
    const { processSelectedFiles, resetFileInput } = await import('@/lib/photoInputUtils');
    const files = await processSelectedFiles(target.files);
    resetFileInput(target);
    if (!files.length) return;
    setStockImageUrl?.(null);
    const [first, ...rest] = files;
    setNewImages([first]);
    setNewImagePreviews([URL.createObjectURL(first)]);
    if (rest.length && onAddFiles) {
      await onAddFiles(rest);
    }
  };

  // Multi-photo "Add more" handler — appends via parent's addRawFiles when wired.
  const handleAddMore = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const rawFiles = Array.from(input.files || []);
    input.value = '';
    if (!rawFiles.length || !onAddFiles) return;
    await onAddFiles(rawFiles);
  };

  const removeHeroPhoto = () => {
    if (uploadedPhoto) URL.revokeObjectURL(uploadedPhoto);
    if (onRemoveUploaded && newImagePreviews.length > 0) {
      onRemoveUploaded(0);
    } else {
      setNewImages([]);
      setNewImagePreviews([]);
    }
    if (isStock) {
      onRemoveStock ? onRemoveStock() : setStockImageUrl?.(null);
    }
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {addedCount > 0 && (
        <div className="text-xs text-muted-foreground -mb-1">
          {addedCount} added this session
        </div>
      )}

      {/* Photo hero — split into two side-by-side choices when empty */}
      <input ref={fileInputRef} type="file" accept="image/*,.heic,.heif,.dng" multiple className="hidden" onChange={handleHeroPhoto} />
      <input ref={cameraInputRef} type="file" accept="image/*,.heic,.heif,.dng" capture="environment" className="hidden" onChange={handleHeroPhoto} />

      <div
        className={cn(
          'relative w-full rounded-2xl overflow-hidden field-surface field-border flex',
          // Tall preview only when a photo exists; compact action row when empty.
          photo ? 'aspect-[4/3] sm:aspect-auto sm:h-44' : 'h-20 sm:h-24'
        )}
      >
        {photo ? (
          <>
            <img src={photo} alt="" className="absolute inset-0 w-full h-full object-contain" />
            {isStock && (
              <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/70 text-[10px] font-medium text-white/90 uppercase tracking-wide">
                Stock photo
              </span>
            )}
            <button
              type="button"
              onClick={removeHeroPhoto}
              className="absolute top-2 right-2 p-1.5 rounded-full bg-black/70 hover:bg-black/85"
              aria-label="Remove photo"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex-1 flex flex-row items-center justify-center gap-3 px-3 hover:bg-white/[0.03] transition-colors"
            >
              <Camera className="w-5 h-5 text-muted-foreground shrink-0" strokeWidth={1.75} />
              <div className="flex flex-col items-start min-w-0">
                <span className="text-sm font-medium text-foreground/85 leading-tight">Add photo</span>
                <span className="text-[11px] text-muted-foreground/70 leading-tight">Capture or choose</span>
              </div>
            </button>

            <div className="relative w-px flex flex-col items-center justify-center py-3">
              <div className="flex-1 w-px bg-white/[0.06]" />
              <span className="my-1.5 text-[9px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                or
              </span>
              <div className="flex-1 w-px bg-white/[0.06]" />
            </div>

            {onOpenStockPicker && (
              <button
                type="button"
                onClick={onOpenStockPicker}
                className="flex-1 flex flex-row items-center justify-center gap-3 px-3 hover:bg-white/[0.03] transition-colors"
              >
                <Images className="w-5 h-5 text-muted-foreground shrink-0" strokeWidth={1.75} />
                <div className="flex flex-col items-start min-w-0">
                  <span className="text-sm font-medium text-foreground/85 leading-tight">Stock photo</span>
                  <span className="text-[11px] text-muted-foreground/70 leading-tight">Search library</span>
                </div>
              </button>
            )}
          </>
        )}
      </div>

      {/* Thumbnail strip — only renders when 2+ photos exist (hero already shows the first). */}
      {hasAnyPhoto && orderableImages.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {orderableImages.map((img, idx) => (
            <div
              key={img.type === 'stock' ? 'stock' : `new-${img.index}`}
              className="relative w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden field-surface field-border"
            >
              <img src={img.url} alt="" className="w-full h-full object-cover" />
              {img.type === 'stock' && (
                <span className="absolute bottom-0.5 left-0.5 px-1 py-0 text-[8px] font-medium uppercase tracking-wide bg-black/70 text-white/85 rounded">
                  Stock
                </span>
              )}
              {idx === 0 && (
                <span className="absolute top-0.5 left-0.5 px-1 py-0 text-[8px] font-medium bg-primary text-primary-foreground rounded">
                  Cover
                </span>
              )}
              <button
                type="button"
                onClick={() => {
                  if (img.type === 'stock') {
                    onRemoveStock?.();
                  } else {
                    onRemoveUploaded?.(img.index);
                  }
                }}
                className="absolute top-0.5 right-0.5 p-0.5 bg-black/70 rounded-full hover:bg-black/90 transition-colors"
                aria-label="Remove photo"
              >
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* "Add more" chips — field-surface tokens to match Quantity / Title / Language inputs.
          Only shown once 1+ photos exist; the empty-state hero already covers the first add. */}
      {hasAnyPhoto && canAddMore && onAddFiles && (
        <>
          <input
            ref={addMoreInputRef}
            type="file"
            accept="image/*,.heic,.heif,.dng"
            multiple
            className="hidden"
            onChange={handleAddMore}
          />
          <input
            ref={addMoreCameraRef}
            type="file"
            accept="image/*,.heic,.heif,.dng"
            capture="environment"
            className="hidden"
            onChange={handleAddMore}
          />
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => addMoreCameraRef.current?.click()}
              className="h-14 rounded-2xl field-surface field-border flex items-center justify-center gap-2 text-sm font-medium text-foreground/80 hover:bg-white/[0.03] transition-colors"
            >
              <Camera className="w-4 h-4 text-muted-foreground" />
              Take
            </button>
            <button
              type="button"
              onClick={() => addMoreInputRef.current?.click()}
              className="h-14 rounded-2xl field-surface field-border flex items-center justify-center gap-2 text-sm font-medium text-foreground/80 hover:bg-white/[0.03] transition-colors"
            >
              <Upload className="w-4 h-4 text-muted-foreground" />
              Upload
            </button>
            <button
              type="button"
              onClick={onOpenStockPicker}
              disabled={!onOpenStockPicker || Boolean(stockImageUrl)}
              className="h-14 rounded-2xl field-surface field-border flex items-center justify-center gap-2 text-sm font-medium text-foreground/80 hover:bg-white/[0.03] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Images className="w-4 h-4 text-muted-foreground" />
              Sample
            </button>
          </div>
        </>
      )}

      {/* Game + Type */}
      <div className="grid grid-cols-2 gap-3">
        <FormField control={form.control} name="game" render={({ field }) => (
          <FormItem>
            <FormControl>
              <FloatingSelectField label="Game" hasValue={Boolean(field.value)}>
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="floating-trigger h-14"><SelectValue placeholder=" " /></SelectTrigger>
                  <SelectContent>
                    {TRADING_CARD_GAMES.map((g) => (
                      <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FloatingSelectField>
            </FormControl>
            <FormMessage />
          </FormItem>
        )} />
        <FormField control={form.control} name="category" render={({ field }) => (
          <FormItem>
            <FormControl>
              <FloatingSelectField label="Type" hasValue={Boolean(field.value)}>
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="floating-trigger h-14"><SelectValue placeholder=" " /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c: { value: string; label: string }) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FloatingSelectField>
            </FormControl>
            <FormMessage />
          </FormItem>
        )} />
      </div>

      {/* Title */}
      <FormField control={form.control} name="title" render={({ field }) => (
        <FormItem>
          <FormControl>
            <FloatingInput label="Title (optional)" placeholder="e.g. Charizard VMAX Rainbow" {...field} value={field.value || ''} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )} />

      {/* Match candidate slot — hidden today.
          Future: when useCardMatchCandidates returns candidates from Scrydex,
          render <MatchCandidateSlot candidates={candidates} onPick={...} /> here.
          See src/lib/cardMatching.ts + memory: scrydex-matching-workbench-v1. */}
      {candidates.length > 0 ? (
        <div className="text-xs text-muted-foreground">
          {candidates.length} match{candidates.length === 1 ? '' : 'es'}
        </div>
      ) : null}

      {/* Slab-only: grading company + grade */}
      {watchCategory === 'slabs' && (
        <div className="grid grid-cols-2 gap-3">
          <FormField control={form.control} name="grading_company" render={({ field }) => (
            <FormItem>
              <FormControl>
                <FloatingSelectField label="Grading co." hasValue={Boolean(field.value)}>
                  <Select onValueChange={field.onChange} value={field.value || ''}>
                    <SelectTrigger className="floating-trigger h-14"><SelectValue placeholder=" " /></SelectTrigger>
                    <SelectContent>
                      {GRADING_COMPANIES.map((g: { value: string; label: string }) => (
                        <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FloatingSelectField>
              </FormControl>
              <FormMessage />
            </FormItem>
          )} />
          <FormField control={form.control} name="slab_grade" render={({ field }) => (
            <FormItem>
              <FormControl>
                <FloatingInput
                  label="Grade"
                  type="number"
                  step="0.5"
                  min="1"
                  max="10"
                  placeholder="10"
                  {...field}
                  value={field.value ?? ''}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )} />
        </div>
      )}

      {/* Inline Price + List controls (Convergence Plan Phase 3.1).
          Only rendered while the "expanded" section is closed — once expanded,
          the full pricing block below owns these fields (same react-hook-form
          names, so state stays in sync). */}
      {!expanded && (
        <div className="space-y-3">
          <FormField
            control={form.control}
            name="asking_price"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-muted-foreground/60 z-10 flex items-center justify-center w-4">
                      $
                    </span>
                    <Input
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*\.?[0-9]*"
                      {...PRICE_INPUT_PROPS}
                      placeholder="Asking price (optional if open to offers)"
                      className="h-14 pl-9 text-base field-surface field-border placeholder:text-muted-foreground/60"
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value.replace(/[^0-9.]/g, ''))}
                    />
                  </div>
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="is_listed"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between h-14 px-3 rounded-2xl field-surface field-border">
                  <div className="flex items-center gap-3 min-w-0">
                    <Tag className="w-5 h-5 text-muted-foreground/60 shrink-0" />
                    <span className="text-sm font-medium truncate">List for sale</span>
                  </div>
                  <Switch checked={!!field.value} onCheckedChange={field.onChange} />
                </div>
              </FormItem>
            )}
          />

          {(watchIsListed || watchIsNegotiable) && (
            <FormField
              control={form.control}
              name="is_negotiable"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between h-14 px-3 rounded-2xl field-surface field-border">
                    <div className="flex items-center gap-3 min-w-0">
                      <MessageCircle className="w-5 h-5 text-muted-foreground/60 shrink-0" />
                      <span className="text-sm font-medium truncate">Open to offers</span>
                    </div>
                    <Switch checked={!!field.value} onCheckedChange={field.onChange} />
                  </div>
                </FormItem>
              )}
            />
          )}
        </div>
      )}

      {/* Continue editing disclosure — only rare fields left after inlining
          Price + List above. Copy updated per Convergence Plan Phase 3.1. */}
      {onContinueEditing && (
        <button
          type="button"
          onClick={onContinueEditing}
          className={cn(
            'w-full h-14 flex items-center gap-3 rounded-2xl px-4',
            'field-surface field-border text-left transition-colors',
            'text-sm font-medium text-foreground/80 hover:text-foreground',
          )}
        >
          <Plus className="w-4 h-4 text-muted-foreground shrink-0" />
          <span className="flex-1 truncate">More details (cost basis, condition, proof…)</span>
          <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
        </button>
      )}


    </div>
  );
}
